/* eslint-disable i18next/no-literal-string */
import { useEffect, useRef, useState, type RefObject } from "react";
import { cn } from "~/lib/utils";
import { colors } from "~/components/landing/colors";
import VehicleMarker from "./VehicleMarker";
import AnimatedSpeeds from "./AnimatedSpeeds";

/**
 * Ported from bouncieweb's
 * `LandingPageComponents/ActiveTripExample/ActiveTripResponsiveExample.js`
 * (the responsive variant used by the Speed bento item).
 *
 * A live "Driving in Progress" notification: a pulsing vehicle marker, a clock
 * that updates every 5s, and an MPH readout whose digits roll (AnimatedSpeeds)
 * through a fixed loop of speeds, turning red at/above 60mph.
 *
 * The original used `useOnScreen` (currently-visible) so the interval only runs
 * while the card is on screen. The new project only ported `useHasBeenOnScreen`,
 * so we inline a small IntersectionObserver-based `useOnScreen` here (client-only,
 * inside useEffect — SSR safe).
 */

function useOnScreen(ref: RefObject<Element | null>): boolean {
  const [isIntersecting, setIntersecting] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(([entry]) =>
      setIntersecting(entry.isIntersecting),
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref]);
  return isIntersecting;
}

const speeds = [58, 45, 64, 55, 57, 66];
const changeInterval = 5 * 1000;

export default function ActiveTripExample() {
  const speedIndex = useRef(0);
  const [tens, setTens] = useState(4);
  const [ones, setOnes] = useState(5);
  const [isRed, setIsRed] = useState(false);
  const [time, setTime] = useState<string>();
  const ref = useRef<HTMLDivElement>(null);
  const isOnScreen = useOnScreen(ref);

  useEffect(() => {
    if (!isOnScreen) return;
    const updateTimeAndSpeed = () => {
      setTime(
        new Date().toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
        }),
      );

      let newIndex = speedIndex.current + 1;
      if (newIndex > speeds.length - 1) newIndex = 0;
      speedIndex.current = newIndex;

      const newSpeed = speeds[speedIndex.current];
      setIsRed(newSpeed >= 60);

      const [newTens, newOnes] = String(newSpeed)
        .split("")
        .map((char) => parseInt(char, 10));
      setTens(newTens);
      setOnes(newOnes);
    };
    updateTimeAndSpeed();
    const id = setInterval(updateTimeAndSpeed, changeInterval);
    return () => clearInterval(id);
  }, [isOnScreen]);

  return (
    // Notification — responsive: column on mobile, row md+
    <div
      ref={ref}
      className={cn(
        "m-4 mx-0 flex w-full flex-col items-center justify-between rounded-[8px] p-[0.4rem] leading-[1.3]",
        "bg-white/70 [box-shadow:0_5px_10px_rgba(0,0,0,.1)] [text-wrap:nowrap]",
        "text-[3vw] min-[431px]:text-[13px]",
        "sm:p-4 sm:text-[16px]",
        "md:flex-row md:p-2 md:text-left md:text-[15px]",
        "lg:mt-8 lg:text-[13px]",
        "xl:text-[15px]",
      )}
    >
      <VehicleMarker rotate="rotateZ(45deg)" />
      {/* Status */}
      <div className="px-0 py-1 [text-wrap:nowrap] sm:py-2 md:p-0">
        <div className="inline-block [font-family:var(--font-sans)] font-bold">
          Driving in Progress
        </div>
        <br />
        {/* Time */}
        <div
          className="w-[11.5em] text-[0.85em] [text-wrap:nowrap]"
          style={{ color: colors.blue }}
        >
          Updated at {time}
        </div>
      </div>
      {/* Speed */}
      <div
        className="text-center font-bold"
        style={isRed ? { color: colors.red } : undefined}
      >
        {/* SpeedNumber */}
        <div className="w-[1.3em] text-[24px] leading-[1em] min-[431px]:text-[26px] sm:text-[28px]">
          <AnimatedSpeeds tens={tens} ones={ones} />
        </div>
        {/* SpeedUnit */}
        <div className="transition-all duration-[0.75s] ease-out">MPH</div>
      </div>
    </div>
  );
}
